// Uploading a project's schema and rules (ported from site/dash/main.ts with no change in behaviour):
// schema.ts is turned into schema.json in a Web Worker, rules are compiled before upload, and both
// halves of the schema go in one batch so they never disagree.
import { doc, serverTimestamp, updateDoc, writeBatch, type DocumentData } from "@estra/client/firestore";
import { compileRules } from "@estra/rules-firestore";
import { backendFiles, type StarterId } from "@estra/create";
import { db } from "../../api.ts";
import { precheckSchema, type KitProject } from "../../lib/kit.ts";

export const FILES = [
  { name: "schema.ts", required: true, hint: "From the schema command, reviewed." },
  { name: "firestore.rules", required: true, hint: "Your rules, unchanged." },
  { name: "storage.rules", required: false, hint: "Only if you use Cloud Storage." },
  { name: "firestore.indexes.json", required: false, hint: "Optional; helps the schema draft." },
] as const;
export type FileName = (typeof FILES)[number]["name"];
/** What a project needs before its backend can be requested: the schema (and its JSON) and the rules. */
export const READY_FILES = ["schema.ts", "schema.json", "firestore.rules"];
export const LOCKED = ["queued", "provisioning", "ready"];

export const isFresh = (p: DocumentData) => p.source === "fresh";
/** A fresh project's backend stays live while new files are applied. */
export const isLive = (p: DocumentData) => p.status === "ready" || (isFresh(p) && Boolean(p.apiUrl));
export const kitProject = (p: DocumentData): KitProject => ({
  name: p.name, slug: p.slug, source: isFresh(p) ? "fresh" : "firebase", starter: p.starter, firebaseProjectId: p.firebaseProjectId ?? "",
  platforms: p.platforms ?? ["web"], keepFirebaseAuth: !isFresh(p) && p.keepFirebaseAuth !== false, apiUrl: p.apiUrl,
});
/** Whether uploads are closed: a fresh project's files stay open once live (re-provisioning applies them). */
export const filesLocked = (p: DocumentData) => (isFresh(p) ? ["queued", "provisioning"].includes(p.status) : LOCKED.includes(p.status));

export function schemaJsonOf(src: string): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const w = new Worker(new URL("../../lib/schema-worker.ts", import.meta.url), { type: "module" });
    const done = (fn: () => void) => {
      clearTimeout(timer);
      w.terminate();
      fn();
    };
    const timer = setTimeout(() => done(() => reject(new Error("Reading schema.ts took too long."))), 5000);
    w.onmessage = (e: MessageEvent<{ json?: unknown; error?: string }>) => done(() => (e.data.error ? reject(new Error(e.data.error)) : resolve(e.data.json)));
    w.onerror = (e) => done(() => reject(new Error(e.message || "schema.ts couldn't be read.")));
    w.postMessage(src);
  });
}

export async function uploadFile(id: string, p: DocumentData, name: string, file: File, progress: (s: string) => void): Promise<void> {
  const text = await file.text();
  if (text.length > 500_000) throw new Error("Files must be under 500 KB.");
  let json: string | null = null;
  if (name === "schema.ts") {
    const bad = precheckSchema(text);
    if (bad) throw new Error(bad);
    progress("Reading the schema…");
    json = JSON.stringify(await schemaJsonOf(text));
  } else if (name.endsWith(".rules")) {
    try {
      compileRules(text, name === "storage.rules" ? { bucket: "default" } : undefined);
    } catch (e) {
      throw new Error(`${name} doesn't compile: ${(e as Error).message}`);
    }
  } else {
    JSON.parse(text);
  }
  progress("Uploading…");
  const b = writeBatch(db);
  b.set(doc(db, `projects/${id}/files/${name}`), { name, text, size: text.length, uploadedAt: serverTimestamp() });
  if (json !== null) b.set(doc(db, `projects/${id}/files/schema.json`), { name: "schema.json", text: json, size: json.length, uploadedAt: serverTimestamp() });
  await b.commit();
  const files = { ...(p.files ?? {}), [name]: { size: text.length, at: Date.now() }, ...(json !== null ? { "schema.json": { size: json.length, at: Date.now() } } : {}) };
  const both = READY_FILES.every((f) => f in files);
  const status = p.status === "draft" && both ? "files" : p.status === "failed" ? "files" : p.status;
  // The whole map, not a dotted path: file names contain dots.
  await updateDoc(doc(db, `projects/${id}`), { files, status, updatedAt: serverTimestamp() });
}

/** Writes the starter's schema and rules into a fresh project (after it exists: the files rule reads it) and queues it. */
export async function queueFresh(id: string, starter: string): Promise<void> {
  const files = backendFiles(starter as StarterId);
  const b = writeBatch(db);
  for (const [name, text] of Object.entries(files)) b.set(doc(db, `projects/${id}/files/${name}`), { name, text, size: text.length, uploadedAt: serverTimestamp() });
  const map = Object.fromEntries(Object.entries(files).map(([name, text]) => [name, { size: text.length, at: Date.now() }]));
  b.update(doc(db, `projects/${id}`), { status: "files", files: map, updatedAt: serverTimestamp() });
  await b.commit();
  await updateDoc(doc(db, `projects/${id}`), { status: "queued", queuedAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

/** "Request my backend" / "Apply the new files": queue the project (a fresh one without files gets the starter's). */
export async function requestBackend(id: string, p: DocumentData): Promise<void> {
  const files = (p.files ?? {}) as Record<string, unknown>;
  const hasBoth = READY_FILES.every((f) => f in files);
  if (isFresh(p) && !hasBoth) return queueFresh(id, String(p.starter ?? "blank"));
  const ref = doc(db, `projects/${id}`);
  if (p.status !== "files") await updateDoc(ref, { status: "files", updatedAt: serverTimestamp() });
  await updateDoc(ref, { status: "queued", queuedAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

export const fmtSize = (n: number) => (n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`);
